import { useTranslation } from 'react-i18next'

import { cn } from '../cn'
import { ToggleSwitch } from '../forms/Toggle'

// Reglas dimension (design/components/bot-config-personality.html §11.2).
// SensitivityRuleRow: a single sensitivity/priority rule. Platform-defined
// rules are "No-negociable" (lock, no toggle); operator rules are "Ajustable"
// (on/off). "No-negociable" is communicated by text + lock icon, never by a
// disabled control alone (a11y). Handoff trigger phrases use PhraseListEditor.

function LockIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="4" y="11" width="16" height="9.5" rx="2.2" />
      <path d="M7.5 11V7.5a4.5 4.5 0 0 1 9 0V11" />
    </svg>
  )
}

export interface SensitivityRuleRowProps {
  title: string
  detail: string
  /** Platform rule that cannot be turned off (shows a lock, no toggle). */
  locked: boolean
  /** Only meaningful when not locked. */
  enabled?: boolean
  onToggle?: (enabled: boolean) => void
  className?: string
}

export function SensitivityRuleRow({
  title,
  detail,
  locked,
  enabled = true,
  onToggle,
  className,
}: SensitivityRuleRowProps) {
  const { t } = useTranslation('components')
  return (
    <div
      className={cn('flex items-center gap-3 border-b border-line py-3 last:border-b-0', className)}
    >
      <span className="flex flex-col gap-0.5">
        <span className="text-[12.5px] font-semibold text-text">{title}</span>
        <span className="text-[11px] text-text-2">{detail}</span>
      </span>
      <span className="ml-auto flex-none">
        {locked ? (
          <span
            className="inline-flex items-center gap-1.5 rounded-pill bg-field px-2.5 py-1 text-[11px] font-bold text-text-2 [&_svg]:size-3.5"
            title={t('rules.lockedReason')}
          >
            <LockIcon />
            {t('rules.nonNegotiable')}
          </span>
        ) : (
          <span className="inline-flex items-center gap-2.5">
            <span className="rounded-pill bg-info-bg px-2.5 py-1 text-[11px] font-bold text-info">
              {t('rules.adjustable')}
            </span>
            <ToggleSwitch
              checked={enabled}
              onChange={(v) => onToggle?.(v)}
              label={`${title}: ${enabled ? t('rules.on') : t('rules.off')}`}
              hideLabel
              size="sm"
            />
          </span>
        )}
      </span>
    </div>
  )
}
